/**
 * What each plan type's reference shows: which of the shared glossary's sections, in
 * what order, and which diagrams.
 *
 * The glossary (`glossary.ts`) is one list of terms for the whole site, each tagged
 * with the plan types it belongs to. A type's reference is a view of it: the sections
 * in the order that type is built, each holding only the terms tagged with the type,
 * and a section left with none is not shown. So `/sheds/reference/` and a deck's
 * reference beside it can share "Joist" without either keeping a copy.
 */

import { SHED_FLOOR_HOW_TO_PATH } from "../site/paths.js";
import type { PlanType } from "../site/planTypes.js";
import { GLOSSARY, type Section, type Term } from "./glossary.js";

/** The reference's framing diagrams (`diagrams.tsx`), by name. */
export type DiagramId = "wall" | "rafter" | "layout";

export interface ReferenceSpec {
  type: PlanType;
  /** The page's heading. */
  title: string;
  /** The sentence under it. */
  intro: string;
  /** Glossary section ids, in reading order. */
  sections: readonly string[];
  diagrams: readonly DiagramId[];
  /**
   * The how-tos (`film/films.ts`, kind `how-to`) that teach what a section defines, each
   * linked under that section's blurb. Its title is the film's own, and
   * `test/film.test.ts` holds both to the film: every how-to is linked from its type's
   * page, under the title its page carries.
   */
  watch: readonly Watch[];
}

/** A how-to linked from a section of the reference. */
export interface Watch {
  /** The glossary section it teaches. */
  section: string;
  path: string;
  title: string;
}

export const REFERENCES: Record<PlanType, ReferenceSpec> = {
  shed: {
    type: "shed",
    title: "Shed reference",
    intro:
      "The vocabulary of light-frame construction, and where to read further. General " +
      "reference — not instructions for any particular building.",
    sections: ["walls", "layout", "openings", "roof", "floor", "sheet"],
    diagrams: ["wall", "rafter", "layout"],
    watch: [{ section: "floor", path: SHED_FLOOR_HOW_TO_PATH, title: "How to frame a shed floor: skids vs. piers vs. a slab" }],
  },
};

/** A section as one plan type's reference shows it: only that type's terms. */
export interface ShownSection extends Omit<Section, "terms"> {
  terms: Term[];
}

/** The sections a type's reference shows, in its order, each with its own terms only. */
export function referenceSections(type: PlanType): ShownSection[] {
  return REFERENCES[type].sections.flatMap((id) => {
    const section = GLOSSARY.find((candidate) => candidate.id === id);
    if (section === undefined) {
      throw new Error(`the ${type} reference names a section the glossary has not got: ${id}`);
    }
    const terms = section.terms.filter((term) => term.types.includes(type));
    return terms.length === 0 ? [] : [{ ...section, terms }];
  });
}
